import useSWR from 'swr';

import { buildQuery, fetcher } from 'api/utils';
import { IDetailResponse, IPaginatedResponse } from 'utils/types';

export type TransferRequestStatus = 'new' | 'in_progress' | 'completed' | 'declined' | 'cancelled';

export type TransferRequestType =
  | 'BR-PT'
  | 'BR-CashCred'
  | 'BR-SMAC'
  | 'CC-BRCP'
  | 'CC-CashCred'
  | 'CC-SMAC'
  | 'airmile'
  | 'hotel_loyalty'
  | 'membership_fee'
  | 'paper'
  | 'evoucher'
  | 'cash_credit'
  | 'CC-HOPE';

export interface ICcmResult {
  success: boolean;
  message: string | null;
}

export interface ITransferRequest {
  id: number;
  state: TransferRequestStatus;
  amount: number;
  created_at: string | Date;
  reason?: string;
  receiver: string;
  request_type: TransferRequestType;
  sender: string;
  updated_at: string;
  card_type?: string;
  statement_date?: string | Date;
  supplementary?: boolean;
  quantity?: number;
  program?: {
    lrs_reward_code: string;
    item_type: string;
  };
  card_item?: {
    name: string;
    item_type: string;
    lrs_reward_code: string;
    currency: string;
    conversion_rate: number;
  };
  card?: {
    name: string;
    product_code: string;
  };
  to_receive?: number;
  ccm_result?: ICcmResult;
  properties: {
    account_number?: string;
    state: TransferRequestStatus;
    amount: string;
    sender: string;
    receiver: string;
    request_type: TransferRequestType;
    sender_last_name: string;
    sender_cif_number: string;
    sender_first_name: string;
    receiver_last_name: string;
    receiver_cif_number: string;
    receiver_first_name: string;
    updated_by: string;
    updated_by_full_name: string;
    employee_id: string;
    receiver_suffix: string;
    conversion_rate?: string | number;
    senders_stored_value_transaction_id?: number;
    sign?: string;
    sender_cif?: string;
    updated_at?: string;
    mask_sender?: string;
    receiver_cif?: string;
    mask_receiver?: string;
    destination_card?: string;
    destination_text?: string;
    transferred_from?: string;
    reason?: string;
    lrsRewardCode?: string;
    product_code?: string;
    card_name?: string;
    hotel_loyalty_name?: string;
    voucher_name?: string;
    airmile_name?: string;
    currency?: string;
    card_item_name?: string;
  };
}

export const useTransferRequest = (id: number | string) => {
  const { data, isLoading, mutate } = useSWR<IDetailResponse<ITransferRequest>>(
    id ? `/v4/dash/custom/bdo/point_transfers/${id}` : null,
    fetcher
  );

  return { data: data?.data, isLoading, mutate };
};

export const useTransferRequestList = (params: object, selectedProductTypes?: string) => {
  const { data, isLoading } = useSWR<IPaginatedResponse<ITransferRequest>>(
    `/v4/dash/custom/bdo/point_transfers?${buildQuery(params)}${
      selectedProductTypes ? `&${selectedProductTypes}` : ''
    }`,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading };
};

export const useTransferReasonList = (params: { state: TransferRequestStatus }) => {
  const { data, isLoading } = useSWR<{ reasons: string[] }>(
    `/v4/dash/custom/bdo/transfer_reasons?${buildQuery(params)}`,
    fetcher
  );

  return { data: data?.reasons, isLoading };
};
